import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import {
  Box,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  Tooltip,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import AdapterMoment from '@mui/lab/AdapterMoment';
import moment, { Moment } from 'moment';

import useTable, {
  defaultRowsPerPageOptions,
  TUseTableState,
} from '../../../../../../components/V2/Table/type1/useTable';
import TableHeadCustom, {
  THeadLabelItem,
} from '../../../../../../components/V2/Table/type1/TableHeadCustom';
import { useAppSelector } from '../../../../../../hooks';
import {
  adminGetPenaltyWaivers,
  getPenaltyWaiverState,
  IPenaltyWaiver,
  resetAdminPenaltyWaiverListData,
  TAdminGetPenaltyWaivers,
} from '../../../../../../store/V2/PenaltyWaiverSlice';
import PenaltyWaiverTableRow from './components/PenaltyWaiverTableRow';
import TableNoData from '../../../../../../components/V2/Empty/TableNoData';
import TableLoader from '../../../../../../components/Loaders/TableLoader';
import TablePaginationActions from '../../../../../../components/V2/Table/type1/TablePaginationActions';
import {
  getLessonHistoryState,
  updateAdminLessonHistoryCommonFilterData,
} from '../../../../../../store/V2/LessonHistorySlice';
import {
  defaultTextFieldProps,
  TPTextField,
} from '../../../../../../components/Inputs';
import TableSelectedActions from '../../../../../../components/V2/Table/type1/TableSelectedActions';
import SelectedActions from './components/SelectedActions';
import ViewPenaltyWaiverModal from './modals/ViewPenaltyWaiverModal';
import UserListDropdown, {
  TUserSelectOption,
} from '../../../../../../components/Inputs/admin/UserListDropdown';
import CourseListDropdown, {
  TCourseSelectOption,
} from '../../../../../../components/Inputs/admin/CourseListDropdown';
import { checkUserPermission } from '../../../../../../helper';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../../store/V2/RolesSlice';
import { getAuthState } from '../../../../../../store/AuthSlice';
import TutorPaymentDetailsCard from './components/TutorPaymentDetailsCard';
import PenaltyDataSummaryView from './components/PenaltyDataSummaryView';
import { preparePenaltyWaiverCSVExportData } from '../../../helper';

export type TState = {
  startDate: Moment | null;
  endDate: Moment | null;
  selectedTutor: TUserSelectOption | null;
  selectedStudent: TUserSelectOption | null;
  selectedCourse: TCourseSelectOption | null;
};

export type TFilterData = {
  limit: number;
  offset: number;
  sortKey: string;
  sortValue: number;
  startDate?: string;
  endDate?: string;
  tutorId?: string;
  studentId?: string;
  courseId?: string;
};

const defaultListLimit = 30;
const defaultOrderBy = 'startDate';
const defaultOrder = 'desc';
const defaultTableData: TUseTableState = {
  defaultOrderBy,
  defaultOrder,
  defaultDense: false,
  defaultCurrentPage: 0,
  defaultRowsPerPage: defaultRowsPerPageOptions[0],
  defaultSelected: [],
};
const defaultStartDateMoment = moment();
const defaultEndDateMoment = defaultStartDateMoment.clone().add(1, 'day');

const defaultFilterData: TFilterData = {
  limit: defaultListLimit,
  offset: 0,
  sortKey: defaultOrderBy,
  sortValue: defaultOrder === 'desc' ? -1 : 1,
  startDate: defaultStartDateMoment.startOf('day').toISOString(),
  endDate: defaultEndDateMoment.endOf('day').toISOString(),
};

const defaultState: TState = {
  startDate: defaultStartDateMoment.clone(),
  endDate: defaultEndDateMoment.clone(),
  selectedTutor: null,
  selectedStudent: null,
  selectedCourse: null,
};

const tableHeaderLabels: THeadLabelItem[] = [
  {
    id: 'createdAt',
    label: 'Created At',
    sortable: true,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'course',
    label: 'Course',
    sortable: true,
    minWidth: 150,
    maxWidth: 150,
  },
  {
    id: 'startDate',
    label: 'Class Start',
    sortable: true,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'endDate',
    label: 'Class End',
    sortable: true,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'tutor',
    label: 'Tutor',
    sortable: true,
    minWidth: 150,
    maxWidth: 150,
  },
  {
    id: 'students',
    label: 'Students',
    sortable: false,
    minWidth: 150,
    maxWidth: 150,
  },
  {
    id: 'lessonType',
    label: 'Lesson Type',
    sortable: true,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'paymentType',
    label: 'Payment Type',
    sortable: true,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'amount',
    label: 'Amount',
    sortable: false,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'paymentStatus',
    label: 'Payment Status',
    sortable: false,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'appealStatus',
    label: 'Appeal Status',
    sortable: false,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'action',
    label: 'Action',
    sortable: false,
    align: 'right',
  },
];

const PaymentAdjustmentsTab: FunctionComponent = () => {
  const dispatch = useDispatch();

  // Store
  const authState = useAppSelector(getAuthState);
  const penaltyWaiversState = useAppSelector(getPenaltyWaiverState);
  const lessonHistoryState = useAppSelector(getLessonHistoryState);

  // State
  const tableData = useTable(defaultTableData);
  const [state, setState] = useState({ ...defaultState });
  const [filterData, setFilterData] = useState({ ...defaultFilterData });
  const [viewPenaltyWaiverModalData, setViewPenaltyWaiverModalData] = useState<{
    open: boolean;
    defaultTabIndex?: number;
    penaltyWaiver?: IPenaltyWaiver;
  }>({ open: false });

  const { adminPenaltyWaiverListData } = penaltyWaiversState;
  const { adminLessonHistoryCommonFilterData } = lessonHistoryState;

  const authUser = authState.user;
  const hasTutorAmountDataPermission = checkUserPermission(authUser, {
    adminLessonHistoryCompletedTab: [
      USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_TUTOR_AMOUNT,
    ],
  });

  const tutorAmountData =
    adminPenaltyWaiverListData && adminPenaltyWaiverListData.tutorAmountData
      ? adminPenaltyWaiverListData.tutorAmountData
      : undefined;

  const isPenaltySummaryAlertsVisible = Boolean(
    hasTutorAmountDataPermission &&
      tutorAmountData &&
      tutorAmountData.tutorPenaltyAmountData &&
      Object.keys(tutorAmountData.tutorPenaltyAmountData).length
  );

  let totalCount = 0;
  let penaltyWaiverList: IPenaltyWaiver[] = [];

  if (adminPenaltyWaiverListData) {
    totalCount = adminPenaltyWaiverListData.totalDocs || 0;

    if (
      adminPenaltyWaiverListData.list &&
      adminPenaltyWaiverListData.list.length
    ) {
      penaltyWaiverList = adminPenaltyWaiverListData.list || [];
    }
  }

  useEffect(() => {
    dispatch(resetAdminPenaltyWaiverListData());
    getInitialPenaltyWaiverListData();

    return () => {
      dispatch(resetAdminPenaltyWaiverListData());
    };
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const getInitialPenaltyWaiverListData = () => {
    const initialFilterData: TFilterData = {
      ...defaultFilterData,
    };

    const newStateData: Partial<TState> = {};

    if (adminLessonHistoryCommonFilterData) {
      if (adminLessonHistoryCommonFilterData.startDate) {
        newStateData.startDate = moment(
          adminLessonHistoryCommonFilterData.startDate
        );

        initialFilterData.startDate = moment(
          adminLessonHistoryCommonFilterData.startDate
        )
          .startOf('day')
          .toISOString();
      } else if (adminLessonHistoryCommonFilterData.startDateCleared) {
        newStateData.startDate = null;
        initialFilterData.startDate = undefined;
      }

      if (adminLessonHistoryCommonFilterData.endDate) {
        newStateData.endDate = moment(
          adminLessonHistoryCommonFilterData.endDate
        );

        initialFilterData.endDate = moment(
          adminLessonHistoryCommonFilterData.endDate
        )
          .endOf('day')
          .toISOString();
      } else if (adminLessonHistoryCommonFilterData.endDateCleared) {
        newStateData.endDate = null;
        initialFilterData.endDate = undefined;
      }

      if (adminLessonHistoryCommonFilterData.selectedTutor) {
        newStateData.selectedTutor = {
          ...adminLessonHistoryCommonFilterData.selectedTutor,
        };

        initialFilterData.tutorId =
          adminLessonHistoryCommonFilterData.selectedTutor.value;
      }

      if (adminLessonHistoryCommonFilterData.selectedStudent) {
        newStateData.selectedStudent = {
          ...adminLessonHistoryCommonFilterData.selectedStudent,
        };

        initialFilterData.studentId =
          adminLessonHistoryCommonFilterData.selectedStudent.value;
      }

      if (adminLessonHistoryCommonFilterData.selectedCourse) {
        newStateData.selectedCourse = {
          ...adminLessonHistoryCommonFilterData.selectedCourse,
        };

        initialFilterData.courseId =
          adminLessonHistoryCommonFilterData.selectedCourse.value;
      }
    }

    getPenaltyWaiverListData({ ...initialFilterData });
    updateStateAttributes({ ...newStateData });
  };

  const getPenaltyWaiverListData = async (
    filterDataAttributes: TFilterData
  ) => {
    const apiData: TAdminGetPenaltyWaivers = {
      limit: filterDataAttributes.limit,
      offset: filterDataAttributes.offset,
    };

    if (hasTutorAmountDataPermission) {
      apiData.includeTutorAmountData = true;
    }

    if (filterDataAttributes.sortKey) {
      apiData.sortKey = filterDataAttributes.sortKey;
    }

    if (filterDataAttributes.sortValue) {
      apiData.sortValue = filterDataAttributes.sortValue;
    }

    if (filterDataAttributes.startDate) {
      apiData.startDate = filterDataAttributes.startDate;
    }

    if (filterDataAttributes.tutorId) {
      apiData.tutorId = filterDataAttributes.tutorId;
    }

    if (filterDataAttributes.studentId) {
      apiData.studentId = filterDataAttributes.studentId;
    }

    if (filterDataAttributes.courseId) {
      apiData.courseId = filterDataAttributes.courseId;
    }

    dispatch(adminGetPenaltyWaivers({ ...apiData }));
    setFilterData({ ...filterDataAttributes });
  };

  const handleSort = (id: string) => {
    let newSortValue = 1;

    if (filterData.sortKey === id && filterData.sortValue === 1) {
      newSortValue = -1;
    }

    getPenaltyWaiverListData({
      ...filterData,
      sortValue: newSortValue,
      sortKey: id,
    });

    tableData.onSort(id);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => {
    getPenaltyWaiverListData({
      ...filterData,
      offset: page * filterData.limit,
    });

    tableData.onChangePage(event, page);
    tableData.setSelected([]);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    getPenaltyWaiverListData({
      ...filterData,
      offset: 0,
      limit: parseInt(event.target.value, 10),
    });

    tableData.onChangeRowsPerPage(event);
  };

  const refreshList = () => {
    getPenaltyWaiverListData({ ...filterData });
  };

  const onExport = () => {
    if (authUser) {
      const link = document.createElement('a');
      const filename = 'lesson-history-export.csv';

      let csv = preparePenaltyWaiverCSVExportData({
        list: penaltyWaiverList,
        selectedList: tableData.selected,
        isAdmin: true,
      });

      if (csv == null) return;

      if (!csv.match(/^data:text\/csv/i)) {
        csv = `data:text/csv;charset=utf-8,${csv}`;
      }

      link.setAttribute('href', encodeURI(csv));
      link.setAttribute('download', filename);
      link.click();
    }
  };

  return (
    <div>
      <div style={{ padding: '20px' }}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Grid
            container
            alignItems="center"
            columnSpacing="16px"
            rowSpacing="20px"
          >
            <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
              <FormControl fullWidth>
                <DatePicker
                  clearable
                  value={state.startDate}
                  label="Class Start Date"
                  inputFormat="YYYY-MMM-DD"
                  onChange={(date) => {
                    updateStateAttributes({
                      startDate: date,
                    });

                    getPenaltyWaiverListData({
                      ...filterData,
                      startDate: date
                        ? date.clone().startOf('day').toISOString()
                        : undefined,
                    });

                    dispatch(
                      updateAdminLessonHistoryCommonFilterData({
                        startDate: date ? date.toISOString() : undefined,
                        startDateCleared: date ? undefined : true,
                      })
                    );
                  }}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...defaultTextFieldProps}
                      size="small"
                      placeholder="Start Date"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
              <FormControl fullWidth>
                <DatePicker
                  clearable
                  value={state.endDate}
                  label="Class End Date"
                  inputFormat="YYYY-MMM-DD"
                  onChange={(date) => {
                    updateStateAttributes({
                      endDate: date,
                    });

                    getPenaltyWaiverListData({
                      ...filterData,
                      endDate: date
                        ? date.clone().endOf('day').toISOString()
                        : undefined,
                    });

                    dispatch(
                      updateAdminLessonHistoryCommonFilterData({
                        endDate: date ? date.toISOString() : undefined,
                        endDateCleared: date ? undefined : true,
                      })
                    );
                  }}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...defaultTextFieldProps}
                      size="small"
                      placeholder="End Date"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
              <FormControl fullWidth>
                <UserListDropdown
                  userTypes={['tutor']}
                  selectedItem={state.selectedTutor}
                  textFieldProps={{
                    label: 'Tutor',
                    placeholder: 'Filter by Tutor',
                    ...defaultTextFieldProps,
                  }}
                  handleChange={(e, value) => {
                    updateStateAttributes({
                      selectedTutor: value,
                    });

                    getPenaltyWaiverListData({
                      ...filterData,
                      tutorId: value ? value.value : undefined,
                    });

                    dispatch(
                      updateAdminLessonHistoryCommonFilterData({
                        selectedTutor: value || undefined,
                      })
                    );
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
              <FormControl fullWidth>
                <UserListDropdown
                  userTypes={['student']}
                  selectedItem={state.selectedStudent}
                  textFieldProps={{
                    label: 'Student',
                    placeholder: 'Filter by Student',
                    ...defaultTextFieldProps,
                  }}
                  handleChange={(e, value) => {
                    updateStateAttributes({
                      selectedStudent: value,
                    });

                    getPenaltyWaiverListData({
                      ...filterData,
                      studentId: value ? value.value : undefined,
                    });

                    dispatch(
                      updateAdminLessonHistoryCommonFilterData({
                        selectedStudent: value || undefined,
                      })
                    );
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
              <FormControl fullWidth>
                <CourseListDropdown
                  selectedItem={state.selectedCourse}
                  textFieldProps={{
                    label: 'Course',
                    placeholder: 'Filter by Course',
                    ...defaultTextFieldProps,
                  }}
                  handleChange={(e, value) => {
                    updateStateAttributes({
                      selectedCourse: value,
                    });

                    getPenaltyWaiverListData({
                      ...filterData,
                      courseId: value ? value.value : undefined,
                    });

                    dispatch(
                      updateAdminLessonHistoryCommonFilterData({
                        selectedCourse: value || undefined,
                      })
                    );
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
              <Tooltip title="Refresh List">
                <IconButton onClick={refreshList}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </div>

      <Collapse in={tableData.selected.length > 0}>
        <TableSelectedActions
          selectedList={tableData.selected}
          clearSelected={() => tableData.setSelected([])}
          actionsElement={
            <SelectedActions
              selectedList={tableData.selected}
              refreshList={refreshList}
              onExport={onExport}
            />
          }
        />
      </Collapse>

      <Collapse
        in={
          hasTutorAmountDataPermission &&
          Boolean(filterData.tutorId) &&
          Boolean(penaltyWaiversState.adminPenaltyWaiverListData)
        }
      >
        <TutorPaymentDetailsCard
          startDate={state.startDate}
          endDate={state.endDate}
        />
      </Collapse>

      <Collapse in={isPenaltySummaryAlertsVisible}>
        <PenaltyDataSummaryView
          startDate={state.startDate}
          endDate={state.endDate}
          tutorAmountData={tutorAmountData}
        />
      </Collapse>

      <TableContainer
        sx={{ position: 'relative', maxHeight: 'calc(100vh - 250px)' }}
      >
        <Table stickyHeader size={tableData.dense ? 'small' : 'medium'}>
          <TableHeadCustom
            order={tableData.order}
            orderBy={tableData.orderBy}
            rowCount={penaltyWaiverList.length}
            headLabels={tableHeaderLabels}
            numSelected={tableData.selected.length}
            onSort={handleSort}
            onSelectAllRows={(checked) =>
              tableData.onSelectAllRows(
                checked,
                penaltyWaiverList.map((row) => row._id)
              )
            }
          />
          <TableBody>
            {penaltyWaiverList.map((row) => {
              const isSelected = tableData.selected.includes(row._id);

              return (
                <PenaltyWaiverTableRow
                  key={row._id}
                  penaltyWaiver={row}
                  selected={isSelected}
                  onSelectRow={() => tableData.onSelectRow(row._id)}
                  onView={() =>
                    setViewPenaltyWaiverModalData({
                      open: true,
                      defaultTabIndex: 0,
                      penaltyWaiver: row,
                    })
                  }
                  onStudentsClick={() =>
                    setViewPenaltyWaiverModalData({
                      open: true,
                      defaultTabIndex: 1,
                      penaltyWaiver: row,
                    })
                  }
                  onAppealClick={() =>
                    setViewPenaltyWaiverModalData({
                      open: true,
                      defaultTabIndex: 2,
                      penaltyWaiver: row,
                    })
                  }
                />
              );
            })}
            {penaltyWaiversState.status !== 'loading' &&
            !penaltyWaiverList.length ? (
              <TableNoData title="No adjustments found" />
            ) : null}
            {penaltyWaiversState.status === 'loading' &&
            !penaltyWaiverList.length ? (
              <TableLoader />
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={defaultRowsPerPageOptions}
          component="div"
          count={totalCount}
          rowsPerPage={tableData.rowsPerPage}
          page={tableData.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleRowsPerPageChange}
          ActionsComponent={TablePaginationActions}
        />

        <FormControlLabel
          label="Dense"
          sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
          control={
            <Switch
              checked={tableData.dense}
              onChange={tableData.onChangeDense}
            />
          }
        />
      </Box>
      {/* Modals */}
      {viewPenaltyWaiverModalData.open &&
      viewPenaltyWaiverModalData.penaltyWaiver ? (
        <ViewPenaltyWaiverModal
          open={viewPenaltyWaiverModalData.open}
          onClose={() => setViewPenaltyWaiverModalData({ open: false })}
          penaltyWaiverId={viewPenaltyWaiverModalData.penaltyWaiver._id}
          defaultTabIndex={viewPenaltyWaiverModalData.defaultTabIndex}
        />
      ) : null}
    </div>
  );
};

export default PaymentAdjustmentsTab;
